import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Button from '@mui/material/Button';
import { blue, orange } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import { List } from 'immutable';
import React from 'react';
import { useTranslation, withTranslation } from 'react-i18next';

import { dispatchSiteAssociationActions } from '@sophocles/api/odlApp';
import { fetchRelocationDestinations, postRelocateThirdPartySharedSite } from '@sophocles/api/virtual';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import FormErrorAlert from '@sophocles/components/FormErrorAlert/FormErrorAlert';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import { parseApiError } from '@sophocles/utils/apiErrors';
import { determineIsAtLeastCustomerAdmin, userPermCheck } from '@sophocles/utils/userPermissionUtils';

class MoveSiteDialog extends React.Component {
  constructor(props) {
    super(props);
    const cust = props.siteGroup ? props.siteGroup.get('customer', null) : null;
    this.state = {
      selected: null,
      changeCustomer: false,
      selectedCust: cust,
      errorMessages: [],
      loading: false,
      redirectPrompt: false,
      // null until the destination fetch answers; [] means "answered, and this
      // site has nowhere virtual to go". The picker distinguishes the two.
      virtualDestinations: null,
    };
  }

  /**
   * Turn a rejected relocate/move call into the banner messages to show.
   *
   * Both write paths answer a rejection with a body parseApiError already
   * understands — the virtual relocate with DRF's `{detail}`, the association
   * dispatch with a bare array of `{code, detail}` envelopes — so neither needs
   * its own unwrapping. knownFields is empty because this dialog has no named
   * inputs a field-keyed message could attach to.
   *
   * @param {*} error - the rejected axios error.
   * @returns {string[]} form-wide messages for FormErrorAlert.
   */
  parseSubmitError = error => {
    const { t } = this.props;
    return parseApiError(error, {
      knownFields: [],
      genericMessage: t('relocate.errorOccurred'),
    }).nonFieldMessages;
  };

  componentDidMount() {
    const { site, siteGroup, closeDialog, isVirtual, t } = this.props;
    const siteId = site?.id ?? 0;
    const sgId = siteGroup.get('id', 0);

    if (!isVirtual) {
      return;
    }

    // Map to the autocomplete's option shape here rather than in render: the
    // picker reads fixedData once when it mounts, so the array it receives has
    // to keep a stable identity across the parent's re-renders.
    // `id` is the concrete owner group of the pair and `extra` the virtual one —
    // the relocate payload needs both.
    fetchRelocationDestinations(sgId, siteId)
      .then(destinations => {
        this.setState({
          virtualDestinations: destinations.map(destination => ({
            id: destination.id,
            name: destination.name,
            description: destination.description,
            extra: destination.virtual_id,
          })),
        });
      })
      .catch(() => {
        alert(t('relocate.dataError'));
        closeDialog();
      });
  }

  submit = () => {
    const { site, siteGroup, router, user, isVirtual } = this.props;
    const { selected, selectedVirtual, selectedCust } = this.state;

    this.setState({ loading: true, errorMessages: [] });

    if (isVirtual) {
      const virtualRelocatePayload = {
        site_id: site?.id ?? 0,
        site_group_id: siteGroup.get('id', 0),
        new_site_group_id: selectedVirtual,
        owner_site_group_id: selected,
      };
      postRelocateThirdPartySharedSite(virtualRelocatePayload)
        .then(() => {
          this.setState({ loading: false, redirectPrompt: true });
        })
        .catch(err => {
          this.setState({ loading: false, errorMessages: this.parseSubmitError(err) });
        });
    } else {
      dispatchSiteAssociationActions(site?.id ?? 0, [
        { key: 'move', params: { site_group: { type: 'site-group', id: selected } } },
      ])
        .then(() => {
          if (
            selectedCust !== siteGroup.get('customer', null) &&
            !(userPermCheck(user, 'pliny.view_all_customers') || userPermCheck(user, 'pliny.view_other_customers'))
          ) {
            router.navigate(`/c/${siteGroup.get('customer', null)}/plant/${siteGroup.get('id', null)}/structures`);
          } else {
            this.setState({ loading: false, redirectPrompt: true });
          }
        })
        .catch(err => {
          this.setState({ loading: false, errorMessages: this.parseSubmitError(err) });
        });
    }
  };

  saveSiteGroupValue = id => this.setState({ selected: id });

  saveVirtualSiteGroupValue = id => this.setState({ selectedVirtual: id });

  saveCustomerValue = id => this.setState({ selectedCust: id, selected: null });

  enableCustomerChange = () => this.setState({ changeCustomer: true });

  disableCustomerChange = () => this.setState({ changeCustomer: false });

  render() {
    const {
      open,
      closeDialog,
      isBillboard,
      siteGroup,
      site,
      router,
      user,
      isStaff,
      onMobile,
      t,
      siteTerm,
      siteGroupTerm,
      isVirtual,
    } = this.props;
    const {
      selected,
      selectedVirtual,
      errorMessages,
      redirectPrompt,
      loading,
      changeCustomer,
      selectedCust,
      virtualDestinations,
    } = this.state;

    const customerId = siteGroup.get('customer', null);
    const sgId = siteGroup.get('id', 0);

    // A shared site picks from the virtual destinations the backend paired for
    // it, so its picker waits for that fetch; a real site picks from its
    // customer's groups and only waits on the customer choice.
    const hasNoVirtualDestinations = isVirtual && virtualDestinations?.length === 0;
    const showGroupPicker = isVirtual ? virtualDestinations?.length > 0 : !changeCustomer || !!selectedCust;

    // groups from capabilities.groups (selectCapabilities), falling back to the
    // legacy Immutable user map while capabilities are still loading.
    const groups = this.props.capabilities?.groups ?? user.get('groups', []);

    const canMoveBetweenCustomers =
      !isVirtual &&
      ((user.get('customer', 0) === 16 && determineIsAtLeastCustomerAdmin(groups)) ||
        isStaff ||
        userPermCheck(user, 'pliny.view_other_customers'));

    // TODO(odl-app-contract): site_groups (move destinations list) is not on the
    // site snapshot — BE add needed. Falls back to empty; alreadyGrouped check is
    // conservative (always false) until the field lands.
    const currentSiteGroups = List(site?.site_groups ?? []);

    const alreadyGrouped = currentSiteGroups.some(group => group === selected);

    const actions = [
      <Button key="move-site-cancel" color="secondary" onClick={closeDialog}>
        {t('actions.cancel')}
      </Button>,
      <Button key="move-site-confirm" color="primary" disabled={!selected || alreadyGrouped} onClick={this.submit}>
        {t('actions.submit')}
      </Button>,
    ];

    const currentSiteGroupUrl = `/c/${customerId}/plant/${sgId}/structures`;
    let newSiteGroupUrl = `/c/${selectedCust || customerId}/plant/${selected}/structures`;
    if (isVirtual) {
      newSiteGroupUrl = `/c/${customerId}/plant/${selectedVirtual}/structures`;
    }

    return (
      <Dialog open={open} style={{ overflowY: 'scroll' }} onClose={closeDialog}>
        <DialogTitle>{isBillboard ? t('relocate.moveStructure') : t('relocate.moveSite')}</DialogTitle>
        <DialogContent dividers>
          <div style={{ marginBottom: '20px' }}>
            <div>
              {isBillboard ? t('relocate.instructionsBillboard') : t('relocate.instructionsCommercial')}
              {canMoveBetweenCustomers ? (
                changeCustomer ? (
                  <span style={{ color: blue[500], cursor: 'pointer' }} onClick={this.disableCustomerChange}>
                    {' '}
                    {t('relocate.nevermindBackToOriginal')}
                  </span>
                ) : (
                  <span style={{ color: blue[500], cursor: 'pointer' }} onClick={this.enableCustomerChange}>
                    {' '}
                    {t('relocate.changeCustomer')}
                  </span>
                )
              ) : null}
            </div>

            <div style={{ minHeight: '250px' }}>
              {changeCustomer && (
                <div style={{ paddingBottom: '8px' }}>
                  <CustomerAutocomplete
                    fullWidth
                    value={selectedCust}
                    onChange={(e, customerId) => this.saveCustomerValue(customerId)}
                  />
                </div>
              )}
              <div style={{ paddingBottom: '8px' }}>
                {showGroupPicker && (
                  <SiteGroupAutocomplete
                    excludeInventory
                    fullWidth
                    customerId={changeCustomer ? selectedCust : customerId}
                    fixedData={isVirtual ? virtualDestinations : null}
                    label={t('siteGroups.siteGroup')}
                    value={selected || null}
                    filters={{ is_virtual: false }}
                    onChange={(e, siteGroupId, opt) => {
                      this.saveSiteGroupValue(siteGroupId);
                      if (isVirtual) {
                        this.saveVirtualSiteGroupValue(opt?.extra || null);
                      }
                    }}
                  />
                )}
                {hasNoVirtualDestinations && (
                  <div style={{ color: orange[500] }}>
                    {t('relocate.noVirtualDestinations', {
                      defaultValue:
                        'There is no other {{siteGroupSingular}} this shared {{siteSingular}} can move to. A shared {{siteSingular}} can only move to another shared {{siteGroupSingular}}.',
                      siteGroupSingular: siteGroupTerm.singular.toLowerCase(),
                      siteSingular: siteTerm.singular.toLowerCase(),
                    })}
                  </div>
                )}
                <FormErrorAlert messages={errorMessages} sx={{ mt: 1 }} />

                {alreadyGrouped && (
                  <div style={{ color: orange[500] }}>
                    {isBillboard ? t('relocate.alreadyInPlant') : t('relocate.alreadyInSiteGroup')}
                  </div>
                )}
              </div>

              <div>
                <div>
                  <b>{siteTerm.singular}:</b>
                </div>
                <div style={{ fontSize: '14px' }}>
                  <div style={{ marginLeft: '8px' }}>
                    <b>{t('general.name', 'Name')}:</b> {site?.name ?? ''}
                  </div>
                  <div style={{ marginLeft: '8px' }}>
                    <b>{t('general.description', 'Description')}:</b> {site?.description ?? ''}
                  </div>
                </div>
              </div>
              <div>
                <div>
                  <b>
                    {t('relocate.current', 'Current')} {siteGroupTerm.singular}:
                  </b>
                </div>
                <div style={{ fontSize: '14px' }}>
                  <div style={{ marginLeft: '8px' }}>
                    <b>{t('general.name', 'Name')}:</b> {siteGroup.get('name', '')}
                  </div>
                  <div style={{ marginLeft: '8px' }}>
                    <b>{t('general.description', 'Description')}: </b>
                    {siteGroup.get('description', '')}
                  </div>
                </div>
              </div>

              <p style={{ fontSize: '14px', margin: 'auto 0 30px' }}>
                <i className="fas fa-info-circle" />{' '}
                {t(
                  'relocate.moveInstanceNote',
                  'Note: {{instanceType}} instances of {{sitePlural}} can only be moved to {{instanceType}} {{siteGroupPlural}}. If you do not see the desired {{siteGroupSingular}} in this list, it is probably a {{otherInstanceType}} {{siteGroupSingular}} or you do not have access to it.',
                  {
                    instanceType: isVirtual ? t('relocate.virtual', 'virtual') : t('relocate.physical', 'physical'),
                    otherInstanceType: isVirtual
                      ? t('relocate.physical', 'physical')
                      : t('relocate.virtual', 'virtual'),
                    sitePlural: siteTerm.plural.toLowerCase(),
                    siteGroupPlural: siteGroupTerm.plural.toLowerCase(),
                    siteGroupSingular: siteGroupTerm.singular.toLowerCase(),
                  },
                )}
              </p>
            </div>
          </div>
          {loading && <LoadingScreen />}
          {redirectPrompt && (
            <RedirectDialog
              stayOption={() => router.navigate(currentSiteGroupUrl)}
              newOption={() => router.navigate(newSiteGroupUrl)}
            />
          )}
        </DialogContent>
        <DialogActions>{actions}</DialogActions>
      </Dialog>
    );
  }
}

const TranslatedMoveSiteDialog = withTranslation()(MoveSiteDialog);

function MoveSiteDialogContainer(props) {
  const siteTerm = useSiteTerm();
  const siteGroupTerm = useSiteGroupTerm();
  return <TranslatedMoveSiteDialog {...props} siteTerm={siteTerm} siteGroupTerm={siteGroupTerm} />;
}

export default MoveSiteDialogContainer;

const RedirectDialog = ({ stayOption, newOption }) => {
  const { t } = useTranslation();
  const siteTerm = useSiteTerm();
  const siteGroupTerm = useSiteGroupTerm();
  return (
    <Dialog open={true} onClose={stayOption}>
      <DialogTitle>{t('relocate.success', 'Success!')}</DialogTitle>
      <DialogContent dividers>
        <div>
          {t(
            'relocate.stayOrGoPrompt',
            'Do you want to stay in this {{siteGroup}} or go to the new one you just moved the {{site}} to?',
            { siteGroup: siteGroupTerm.singular.toLowerCase(), site: siteTerm.singular.toLowerCase() },
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button key="stay" color="secondary" onClick={stayOption}>
          {t('relocate.stayOnCurrent', 'Stay on Current')}
        </Button>
        <Button key="new" color="primary" onClick={newOption}>
          {t('relocate.goToNew', 'Go to New')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
